import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import Slider from "react-slick";
import { filter, map, sumBy } from "lodash";
import { MdOutlineStar } from "react-icons/md";
import { IoChevronDown } from "react-icons/io5";
import { BsChevronLeft, BsChevronRight, BsTaxiFrontFill } from "react-icons/bs";
import { FaHotel, FaStar } from "react-icons/fa";
import MainLayout from "@/Component/MainLayout";
import Breadcrumbs from "@/Component/Common/Breadcrumbs";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchPackageDetailsBySlug } from "@/store/slices/holidaySlice";
import BookingModal from "./BookingModal";
import PackageGallery from "./PackageGallery";
import ShareModal from "./ShareModal";
import DownloadItineraryModal from "./DownloadItineraryModal";
import HolidayActionCards from "./HolidayActionCards";
import EnquiryModal, { ENQUIRY_MODULE } from "@/Component/Common/EnquiryModal";
import PolicyAccordion from "@/Component/Common/PolicyAccordion";
import HolidayPackageSection from "@/Component/Common/HolidayPackageSection";
import appendLoopingSlickDots from "@/Common/LoopingSlickDots";
import NotFound from "@/Component/Module/NotFound";
import useIsDesktop from "@/hooks/useIsDesktop";
import useHideBarWhenSectionVisible from "@/hooks/useHideBarWhenSectionVisible";
import { isInvalidDetailSlug, isMissingHolidayDetail } from "@/utils/detailSlug";
import { fetchSimilarHolidays } from "@/services/api/holidays";
import styles from "./HolidayDetail.module.css";
import { IoMdShareAlt } from "react-icons/io";
import { getCurrencySymbol } from "@/utils/getCurrencySymbol";
import { formatIndianPrice } from "@/utils/formatIndianPrice";
import { scrollToSectionElement } from "@/utils/scrollToSection";
import { SiMealie } from "react-icons/si";
import { BiSolidPlaneAlt } from "react-icons/bi";
import { GiAirBalloon } from "react-icons/gi";
import { PiClockClockwiseFill } from "react-icons/pi";
import DownloadNH from "@/Component/Common/DownloadNH";
import CustomerReviewsFull from "@/Component/Common/CustomerReviewsFull";
import { collectTravelerGalleryImages } from "@/Component/Common/CustomerReviews/reviewUtils";
import { fetchReviewsList } from "@/services/api/common";
import { FaCheck } from "react-icons/fa";
import { FaTimes } from "react-icons/fa";
import dayjs from "dayjs";

const FALLBACK_SLUG = "7days-bhutan-tour-package";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "itinerary", label: "Itinerary" },
  { id: "hotels", label: "Hotels" },
  {
    id: "inclusion",
    label: "Inclusions/Exclusions",
  },
];

function formatPrice(value, isIndia = true) {
  const amount = Number(value) || 0;
  if (isIndia) return `₹ ${amount.toLocaleString("en-IN")}`;
  return `AED ${amount.toLocaleString("en-AE")}`;
}

function asList(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  return [];
}

function buildSlabSelection(option, groupedHotels, thumb) {
  const matchingHotels = groupedHotels
    ?.map((city) => ({
      city: city.city,
      hotels: (city.hotels || []).filter((h) => h.slabId === option.slabId),
    }))
    .filter((c) => c.hotels.length > 0);

  const hotelMatch = matchingHotels?.[0]?.hotels?.[0];
  const hotelImage = hotelMatch?.images?.[0]?.imagePath || thumb;

  return {
    slabId: option.slabId,
    slabName: option?.slabName || "",
    slabLabel: `${option?.slabName || ""} Property`.trim(),
    image: hotelImage,
    fromDate: option?.fromDate || "",
    toDate: option?.toDate || "",
    prices: option?.prices || [],
  };
}

function InclusionsPanel({ pkg, nights, days, className = "" }) {
  const flightSubText = String(pkg.flightSubText || "").trim();

  return (
    <div className={className}>
      {nights ? (
        <div className={styles.durationRow}>
          <span className={styles.durationHead}>
            <PiClockClockwiseFill size={16} />
            Duration:
          </span>
          {nights} Nights / {days} Days
        </div>
      ) : null}
      <div className={styles.inclusionsBox}>
        <p className={styles.inclusionsLabel}>INCLUSIONS</p>
        <div
          className={`${styles.inclusionsGrid} ${flightSubText ? styles.inclusionsGridWithTip : ""
            }`}
        >
          {pkg.isFlightIncluded ? (
            <div className={styles.inclusionItem}>
              <div
                className={`${styles.inclusionItemIcon} ${flightSubText ? styles.flightInclusion : ""
                  }`}
              >
                {flightSubText ? (
                  <span className={styles.flightSubTextTip}>{flightSubText}</span>
                ) : null}
                <BiSolidPlaneAlt size={18} />
              </div>
              Flight
            </div>
          ) : null}
          {pkg.isMealsIncluded ? (
            <div className={styles.inclusionItem}>
              <div className={styles.inclusionItemIcon}>
                <SiMealie size={18} />
              </div>
              Meal
            </div>
          ) : null}
          {pkg.isHotelIncluded ? (
            <div className={styles.inclusionItem}>
              <div className={styles.inclusionItemIcon}>
                <FaHotel size={18} />
              </div>
              Hotel
            </div>
          ) : null}
          {pkg.isTransferIncluded ? (
            <div className={styles.inclusionItem}>
              <div className={styles.inclusionItemIcon}>
                <BsTaxiFrontFill size={18} />
              </div>
              Transfers
            </div>
          ) : null}
          {pkg.isActivityIncluded ? (
            <div className={styles.inclusionItem}>
              <div className={styles.inclusionItemIcon}>
                {/* <GiAirBalloon size={18} /> */}
                <Image src={"/assets/icons/activityicon.svg"} alt="" height={20} width={20} quality={70} loading="lazy" />
              </div>
              Activity
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function stripHtml(html = "") {
  return String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function CustomPrevArrow({ onClick }) {
  return (
    <button
      type="button"
      className={`${styles.carouselNav} ${styles.carouselPrev}`}
      onClick={onClick}
      aria-label="Previous images"
    >
      <BsChevronLeft size={18} />
    </button>
  );
}

function CustomNextArrow({ onClick }) {
  return (
    <button
      type="button"
      className={`${styles.carouselNav} ${styles.carouselNext}`}
      onClick={onClick}
      aria-label="Next images"
    >
      <BsChevronRight size={18} />
    </button>
  );
}

function ImageCarousel({ images = [], alt = "Itinerary" }) {
  const list = (images || []).filter(Boolean);
  if (!list.length) return null;

  if (list.length === 1) {
    return (
      <div className={`${styles.carouselWrap} ${styles.carouselWrapSingle}`}>
        <div className={styles.carouselSlide}>
          <div className={styles.carouselImgWrap}>
            <Image
              src={list[0]}
              alt={`${alt} 1`}
              fill
              className={styles.carouselImg}
              quality={70}
              loading="lazy"
              sizes="(max-width: 768px) 100vw, 50vw"
            />
          </div>
        </div>
      </div>
    );
  }

  const showNav = list.length > 2;
  const settings = {
    dots: list.length > 1,
    infinite: list.length > 2,
    speed: 500,
    slidesToShow: Math.min(2, list.length),
    slidesToScroll: 1,
    arrows: showNav,
    prevArrow: showNav ? <CustomPrevArrow /> : null,
    nextArrow: showNav ? <CustomNextArrow /> : null,
    dotsClass: `slick-dots ${styles.carouselDots}`,
    appendDots: appendLoopingSlickDots(`slick-dots ${styles.carouselDots}`),
    responsive: [
      {
        breakpoint: 580,
        settings: {
          slidesToShow: Math.min(2, list.length),
          slidesToScroll: 1,
          arrows: list.length > 2,
          dots: list.length > 1,
          infinite: list.length > 2,
          prevArrow: list.length > 2 ? <CustomPrevArrow /> : null,
          nextArrow: list.length > 2 ? <CustomNextArrow /> : null,
        },
      },
    ],
  };

  return (
    <div className={styles.carouselWrap}>
      <Slider {...settings}>
        {list.map((src, index) => (
          <div key={`${src}-${index}`} className={styles.carouselSlide}>
            <div className={styles.carouselImgWrap}>
              <Image
                src={src}
                alt={`${alt} ${index + 1}`}
                fill
                className={styles.carouselImg}
                quality={70}
                loading="lazy"
                sizes="(max-width: 768px) 100vw, 50vw"
              />
            </div>
          </div>
        ))}
      </Slider>
    </div>
  );
}

function FeatureBadges({ features }) {
  if (!features?.length) return null;

  return (
    <div className={styles.featureRowInner}>
      {features.map((feature, index) => (
        <div key={feature.featureName || index} className={styles.packageDetailBadge}>
          {feature.icon ? (
            <div className={styles.packageDetailBadgeImg}>
              <Image
                src={feature.icon}
                alt={feature.featureName || "feature"}
                width={15}
                height={15}
                sizes="(max-width: 768px) 100vw, 15px"
                quality={70}
                loading="lazy"
              />
            </div>
          ) : null}
          {feature.featureName}
        </div>
      ))}
    </div>
  );
}

// function DesktopItinerary({ packageDetail, cityList }) {
//   return (
//     <div className={styles.itineraryLine}>
//       {map(packageDetail?.groupedHotels, (hotel, index) => (
//         <span key={hotel.city || index}>
//           <span style={{ fontWeight: "600" }}>
//             {sumBy(
//               filter(packageDetail?.cityList, (city) => city.cityId === hotel?.hotels[0]?.cityId),
//               "nights"
//             )}
//             N{" "}
//           </span>
//           {hotel.city}
//           {index + 1 !== packageDetail.groupedHotels?.length && <span> |</span>}
//         </span>
//       ))}
//       {!packageDetail?.groupedHotels?.length &&
//         cityList.map((city, index) => (
//           <span key={city.cityId || index}>
//             <span style={{ fontWeight: "600" }}>{city.nights}N </span>
//             {city.cityName}
//             {index + 1 !== cityList.length && <span> | </span>}
//           </span>
//         ))}
//     </div>
//   );
// }

function MobileItinerary({ packageDetail, cityList }) {
  return (
    <div className={styles.itineraryLine}>
      {cityList.length
        ? cityList.map((city, index) => (
          <span key={city.cityId || index} className={styles.itinerarySegment}>
            <span className={styles.itineraryNights}>{city.nights}N&nbsp;</span> {city.cityName}
            {index + 1 !== cityList.length ? (
              <span className={styles.itineraryDivider}>|</span>
            ) : null}
          </span>
        ))
        : map(packageDetail?.groupedHotels, (hotel, index) => (
          <span key={hotel.city || index} className={styles.itinerarySegment}>
            <span className={styles.itineraryNights}>
              {sumBy(
                filter(
                  packageDetail?.cityList,
                  (city) => city.cityId === hotel?.hotels[0]?.cityId
                ),
                "nights"
              )}
              N &nbsp;
            </span>{" "}
            {hotel.city}
            {index + 1 !== packageDetail.groupedHotels?.length ? (
              <span className={styles.itineraryDivider}>|</span>
            ) : null}
          </span>
        ))}
    </div>
  );
}

export default function HolidayDetail({
  packageSlug,
  initialPackageDetail = null,
  initialReviewsList = null,
}) {
  const dispatch = useAppDispatch();
  const { packageDetail: storeDetail, detailLoading, detailError } = useAppSelector((s) => s.holiday);
  const packageDetail = storeDetail || initialPackageDetail?.data || initialPackageDetail || null;
  const slug = packageSlug || FALLBACK_SLUG;

  const [activeTab, setActiveTab] = useState("overview");
  const [overviewOpen, setOverviewOpen] = useState(true);
  const [exclusionOpen, setExclusionOpen] = useState(true);
  const [openDays, setOpenDays] = useState({});
  const [expandedCards, setExpandedCards] = useState({});
  const [overviewExpanded, setOverviewExpanded] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [selectedSlab, setSelectedSlab] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareData, setShareData] = useState({ url: "", title: "" });
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [similarSectionData, setSimilarSectionData] = useState(null);
  const [reviewsList, setReviewsList] = useState(initialReviewsList);

  const overviewRef = useRef(null);
  const itineraryRef = useRef(null);
  const hotelsRef = useRef(null);
  const inclusionRef = useRef(null);
  const tabSentinelRef = useRef(null);
  const tabsRef = useRef(null);
  const sidebarRef = useRef(null);
  const [showStickyTitle, setShowStickyTitle] = useState(false);

  const { isDesktop } = useIsDesktop(900);
  const [tabsPinned, setTabsPinned] = useState(false);
  const [tabsHeight, setTabsHeight] = useState(0);

  const navbarOffset = isDesktop ? 76 : 72;

  useEffect(() => {
    if (initialPackageDetail || !slug) return;
    dispatch(fetchPackageDetailsBySlug({ HolidaySlug: slug, country: "IN" }));
  }, [dispatch, slug, initialPackageDetail]);

  const packageId = packageDetail?.packageDetails?.packageId;
  const hideMobilePriceBar = useHideBarWhenSectionVisible("detail-hotels", packageId);

  useEffect(() => {
    if (!packageId) {
      setSimilarSectionData(null);
      return undefined;
    }

    let cancelled = false;

    fetchSimilarHolidays(packageId)
      .then((response) => {
        if (!cancelled) setSimilarSectionData(response?.data);
      })
      .catch(() => {
        if (!cancelled) setSimilarSectionData(null);
      });

    return () => {
      cancelled = true;
    };
  }, [packageId]);

  const loadReviews = useCallback(() => {
    if (!slug) return Promise.resolve();

    return fetchReviewsList({ moduleType: "Holiday", slug })
      .then((response) => {
        setReviewsList(response?.data || { reviews: [], summary: {} });
      })
      .catch(() => {
        setReviewsList({ reviews: [], summary: {} });
      });
  }, [slug]);

  useEffect(() => {
    if (initialReviewsList != null || !slug) return undefined;

    let cancelled = false;
    loadReviews().then(() => {
      if (cancelled) return;
    });

    return () => {
      cancelled = true;
    };
  }, [slug, initialReviewsList, loadReviews]);

  useEffect(() => {
    if (isDesktop) {
      setTabsPinned(false);
      return undefined;
    }

    const tabsEl = tabsRef.current;
    const sentinelEl = tabSentinelRef.current;
    if (!tabsEl || !sentinelEl) return undefined;

    const measureTabs = () => {
      setTabsHeight(tabsEl.offsetHeight);
    };

    const onScroll = () => {
      const { top } = sentinelEl.getBoundingClientRect();
      setTabsPinned(top <= navbarOffset);
    };

    measureTabs();
    onScroll();

    const resizeObserver = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(measureTabs)
      : null;
    resizeObserver?.observe(tabsEl);

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", measureTabs);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", measureTabs);
    };
  }, [isDesktop, navbarOffset, packageDetail]);

  const pkg = packageDetail?.packageDetails || {};

  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (!sidebar) return undefined;

    const STICKY_TOP = 90;
    const desktopQuery = window.matchMedia("(min-width: 1101px)");

    const update = () => {
      if (!desktopQuery.matches) {
        setShowStickyTitle(false);
        return;
      }
      setShowStickyTitle(sidebar.getBoundingClientRect().top <= STICKY_TOP + 1);
    };

    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    desktopQuery.addEventListener("change", update);

    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      desktopQuery.removeEventListener("change", update);
    };
  }, [pkg.packageName]);

  const cityList = Array.isArray(packageDetail?.cityList) ? packageDetail.cityList : [];
  const groupedPrices = Array.isArray(packageDetail?.groupedPrices) ? packageDetail.groupedPrices : [];
  const groupedHotels = Array.isArray(packageDetail?.groupedHotels) ? packageDetail.groupedHotels : [];

  const nights = Number(pkg.nights) || 0;
  const days = nights ? nights + 1 : 0;
  const isIndia = Boolean(pkg.isIndia) || Number(pkg.price) > 5000;
  const isDynamicPackage = Boolean(pkg.isDynamicPackage);

  const itinerarySummary =
    cityList
      .map((c) => `${c.nights != null ? `${c.nights}N ` : ""}${c.cityName || ""}`.trim())
      .filter(Boolean)
      .join(" | ") ||
    (pkg.cityName ? `${nights}N ${pkg.cityName}` : "");

  const packageImages = asList(pkg.images);
  const banner =
    packageImages.find((i) => /banner/i.test(i.imageType || ""))?.imagePath ||
    packageImages[0]?.imagePath ||
    "";
  const thumb =
    packageImages.find((i) => /thumb/i.test(i.imageType || ""))?.imagePath ||
    packageImages[1]?.imagePath ||
    banner;

  const reviewImages = useMemo(
    () => collectTravelerGalleryImages(reviewsList?.reviews || [], Infinity),
    [reviewsList]
  );

  const activityImages = asList(pkg.itineraryActivityImages);
  const itineraryDays = asList(pkg.itinerary);
  const inclusions = asList(pkg.inclusions).map(String);
  const exclusions = asList(pkg.exclusions).map(String);
  const featureDetails = pkg.featuredetailsdata;
  const features = asList(featureDetails?.data || featureDetails);
  const showFeatures = Boolean(featureDetails?.featuredetailsdata || features.length);

  const carouselImages = activityImages.map((i) => i.imagePath).filter(Boolean);

  const price = Number(pkg.price || 0);
  const priceOld = Number(pkg.displayCutPrice) || 0;

  useEffect(() => {
    const days = asList(pkg.itinerary);
    if (!days.length) return;
    const init = {};
    days.forEach((d, i) => {
      init[d.itineraryId || d.day] = i === 0;
    });
    setOpenDays(init);
  }, [packageDetail]);

  const overviewText = stripHtml(pkg.shortDescription || "");
  const overviewWords = overviewText.split(/\s+/).filter(Boolean);
  const shortOverview = overviewWords.slice(0, 40).join(" ");

  const [showInclusions, setShowInclusions] = useState(true);
  const [showExclusions, setShowExclusions] = useState(true);
  const scrollToTab = (id) => {
    setActiveTab(id);
    const mapRef = {
      overview: overviewRef,
      itinerary: itineraryRef,
      hotels: hotelsRef,
      inclusion: inclusionRef,
    };
    const el = mapRef[id]?.current;
    if (!el) return;
    scrollToSectionElement(el, { tabsRef, fallbackNavHeight: navbarOffset });
  };

  const scrollToHotels = () => scrollToTab("hotels");

  const handleShareClick = () => {
    if (typeof window === "undefined") return;
    setShareData({
      url: window.location.href,
      title: pkg.packageName || "Package Details",
    });
    setShowShareModal(true);
  };

  const handleOpenBooking = (option = groupedPrices[0]) => {
    if (!option) {
      if (hotelsRef.current) {
        scrollToSectionElement(hotelsRef.current, {
          tabsRef,
          fallbackNavHeight: navbarOffset,
        });
      }
      return;
    }
    setSelectedSlab(buildSlabSelection(option, groupedHotels, thumb));
    setBookingOpen(true);
  };

  if (
    isInvalidDetailSlug(packageDetail) ||
    isInvalidDetailSlug(initialPackageDetail) ||
    (packageDetail && isMissingHolidayDetail(packageDetail)) ||
    (initialPackageDetail && isMissingHolidayDetail(initialPackageDetail))
  ) {
    return <NotFound />;
  }

  if (detailLoading && !packageDetail) {
    return (
      <MainLayout hideHeader>
        <div className={`standard-padding ${styles.page}`}>
          <p className={styles.stateMsg}>Loading package details...</p>
        </div>
      </MainLayout>
    );
  }

  if (detailError || !packageDetail) {
    return <NotFound />;
  }

  return (
    <MainLayout hideHeader>
      <Breadcrumbs />
      <div className={`standard-padding ${styles.page}`}>
        {/* Hero */}
        <div className={styles.titleBlock}>
          <div className={styles.heroDesktop}>
            <div className={styles.titleLine}>
              <h1 className={styles.title}>{pkg.packageName}</h1>
              {nights ? (
                <span className={styles.durationBadge}>
                  {nights}N/{days}D
                </span>
              ) : null}
            </div>
            <div className={styles.itineraryLine}>
              {map(packageDetail?.groupedHotels, (hotel, index) => (
                <span key={index}>
                  <span style={{ fontWeight: "400", color: "#121212" }}>
                    {sumBy(
                      filter(
                        packageDetail?.cityList,
                        (city) => city.cityId === hotel?.hotels[0]?.cityId,
                      ),
                      "nights",
                    )}
                    N{" "}
                  </span>
                  {hotel.city}
                  {index + 1 !== packageDetail.groupedHotels?.length && (
                    <span> |</span>
                  )}
                </span>
              ))}
            </div>
            <div className={styles.headerFooter}>
              <div
                className={`${styles.columnAfterDuration} ${styles.featureRow}`}
              >
                <FeatureBadges features={features} />
              </div>

              <div className={styles.actions}>
                <span
                  role="button"
                  tabIndex={0}
                  className={styles.iconBtn}
                  aria-label="Share"
                  onClick={handleShareClick}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleShareClick();
                    }
                  }}
                >
                  <IoMdShareAlt size={20} />
                </span>
              </div>
            </div>
          </div>

          <div className={styles.heroMobile}>
            <div className={styles.heroTopRow}>
              {nights ? (
                <span className={styles.durationBadge}>
                  {nights}N/{days}D
                </span>
              ) : (
                <span aria-hidden="true" />
              )}

              <div className={styles.heroActions}>
                <span
                  role="button"
                  tabIndex={0}
                  className={styles.iconBtn}
                  aria-label="Share"
                  onClick={handleShareClick}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleShareClick();
                    }
                  }}
                >
                  <IoMdShareAlt size={22} />
                </span>
              </div>
            </div>

            <h1 className={styles.title}>{pkg.packageName}</h1>

            <MobileItinerary
              packageDetail={packageDetail}
              cityList={cityList}
            />

            {features.length ? (
              <div className={styles.featureRow}>
                <FeatureBadges features={features} />
              </div>
            ) : null}
          </div>
        </div>

        <PackageGallery
          packageName={pkg.packageName}
          packageImages={packageImages}
          activityImages={activityImages}
          groupedHotels={groupedHotels}
          reviewImages={reviewImages}
          banner={banner}
          thumb={thumb}
        />

        <InclusionsPanel
          pkg={pkg}
          nights={nights}
          days={days}
          className={`${styles.infoCard} ${styles.mobileInclusions}`}
        />

        <HolidayActionCards
          isIndia={isIndia}
          packageName={pkg.packageName}
          onDownloadClick={() => setDownloadModalOpen(true)}
          className={styles.mobileActionCards}
        />


        <div className={styles.bodyLayout}>
          <div className={styles.mainCol}>
            <div className={styles.tabStickyWrap}>
              <div
                ref={tabSentinelRef}
                className={styles.tabSentinel}
                aria-hidden="true"
              />
              {/* {tabsPinned && tabsHeight ? (
                <div
                  className={styles.tabPlaceholder}
                  style={{ height: tabsHeight }}
                  aria-hidden="true"
                />
              ) : null} */}
              <div
                ref={tabsRef}
                className={`${styles.tabContainer} ${isDesktop ? styles.tabContainerSticky : ""
                  } ${tabsPinned ? styles.tabContainerPinned : ""}`}
              >
                {TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`${styles.tabButton} ${activeTab === tab.id ? styles.tabButtonActive : ""
                      }`}
                    onClick={() => scrollToTab(tab.id)}
                  >
                    {tab.id === "inclusion" ? (
                      <>
                        <span className={styles.desktopTabLabel}>
                          Inclusions/Exclusions
                        </span>

                        <span className={styles.mobileTabLabel}>Incl./Excl.</span>
                      </>
                    ) : (
                      tab.label
                    )}
                  </button>
                ))}
              </div>
            </div>
            {/* Overview */}
            <div
              ref={overviewRef}
              id="detail-overview"
              className={styles.cardSection}
            >
              <button
                type="button"
                className={styles.cardHeader}
                onClick={() => setOverviewOpen((v) => !v)}
              >
                <span className={styles.overviewLabel} />
                <h2 className={styles.cardTitle}>Package Overview</h2>
                <IoChevronDown
                  size={24}
                  className={overviewOpen ? styles.chevronOpen : ""}
                />
              </button>
              {overviewOpen ? (
                <div className={styles.cardBody}>
                  {!overviewExpanded ? (
                    <p className={styles.overviewText}>
                      {shortOverview}
                      {overviewWords.length > 40 ? (
                        <button
                          type="button"
                          className={styles.readMore}
                          onClick={() => setOverviewExpanded(true)}
                        >
                          ...Read More
                        </button>
                      ) : null}
                    </p>
                  ) : (
                    <>
                      <div
                        className={styles.overviewHtml}
                        dangerouslySetInnerHTML={{
                          __html: pkg.shortDescription || "",
                        }}
                      />
                      <button
                        type="button"
                        className={styles.readMore}
                        onClick={() => setOverviewExpanded(false)}
                      >
                        Read Less
                      </button>
                    </>
                  )}
                </div>
              ) : null}
            </div>

            {/* Itinerary */}
            {itineraryDays.length ? (
              <div
                ref={itineraryRef}
                id="detail-itinerary"
                className={styles.cardSection}
              >
                <div className={styles.cardHeaderStatic}>
                  <span className={styles.overviewLabel} />
                  <h2 className={styles.cardTitle}>Itinerary</h2>
                </div>
                <div className={styles.cardBody}>
                  <ImageCarousel images={carouselImages} />
                  <div className={styles.dayList}>
                    {itineraryDays.map((item) => {
                      const id = item.itineraryId || item.day;
                      const open = Boolean(openDays[id]);
                      const dayImgs = activityImages
                        .filter((img) => img.itineraryId === item.itineraryId)
                        .map((img) => img.imagePath);
                      return (
                        <div key={id} className={styles.dayItem}>
                          <button
                            type="button"
                            className={styles.dayHead}
                            onClick={() =>
                              setOpenDays((p) => ({ ...p, [id]: !p[id] }))
                            }
                          >
                            <span className={styles.dayBadge}>
                              Day {item.day}
                            </span>
                            <span className={styles.dayTitle}>
                              {item.title}
                            </span>
                            <IoChevronDown
                              className={open ? styles.chevronOpen : ""}
                              size={20}
                            />
                          </button>
                          {open ? (
                            <div className={styles.dayBody}>
                              <div
                                className={styles.dayDesc}
                                dangerouslySetInnerHTML={{
                                  __html: item.description || "",
                                }}
                              />
                              {item.flightText ? (
                                <div className={styles.metaRow}>
                                  <div className={styles.dayIncIcon}>
                                    <img src="/images/plane-icon.svg" />
                                  </div>
                                  <div style={{ width: "calc(100% - 52px)" }}>
                                    <strong>Flight</strong>
                                    <p>{item.flightText}</p>
                                  </div>
                                </div>
                              ) : null}
                              {item.transferText ? (
                                <div className={styles.metaRow}>
                                  <div className={styles.dayIncIcon}>
                                    <img src="/images/car-icon.svg" />
                                  </div>
                                  <div style={{ width: "calc(100% - 52px)" }}>
                                    <strong>Transfer</strong>
                                    <p>{item.transferText}</p>
                                  </div>
                                </div>
                              ) : null}
                              {item.hotelStayText ? (
                                <div className={styles.metaRow}>
                                  <div className={styles.dayIncIcon}>
                                    <img src="/images/stay-icon.svg" />
                                  </div>
                                  <div style={{ width: "calc(100% - 52px)" }}>
                                    <strong>Hotel Stay</strong>
                                    <p>{item.hotelStayText}</p>
                                  </div>
                                </div>
                              ) : null}
                              {item.activityText ? (
                                <div className={styles.metaRow}>
                                  <div className={styles.dayIncIcon}>
                                    <img
                                      src="/assets/icons/activityInclusion.svg"
                                      style={{ width: "14px" }}
                                    />
                                  </div>
                                  <div style={{ width: "calc(100% - 52px)" }}>
                                    <strong>Activity</strong>
                                    <p>{item.activityText}</p>
                                  </div>
                                </div>
                              ) : null}
                              {item.mealType ? (
                                <div className={styles.metaRow}>
                                  <div className={styles.dayIncIcon}>
                                    <img src="/images/meal-icon.svg" />
                                  </div>
                                  <div style={{ width: "calc(100% - 52px)" }}>
                                    <strong>Meals</strong>
                                    <p>{item.mealType} Included</p>
                                  </div>
                                </div>
                              ) : null}
                              {/* {dayImgs.length ? (
                                <ImageCarousel images={dayImgs} />
                              ) : null} */}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : null}

            {/* Hotels — keep existing card UI */}
            <div
              ref={hotelsRef}
              id="detail-hotels"
              className={styles.cardSection}
            >
              <div className={styles.cardHeaderStatic}>
                <span className={styles.overviewLabel} />
                <h2 className={styles.cardTitle}>
                  {isDynamicPackage ? "Select Hotel" : "Select Departure Date"}
                </h2>
              </div>
              <div className={styles.cardBody}>
                {map(groupedPrices.slice(0, 5), (option) => {
                  const starCount = parseInt(option?.slabName, 10) || 0;
                  const matchingHotels = groupedHotels
                    ?.map((city) => ({
                      city: city.city,
                      hotels: (city.hotels || []).filter(
                        (h) => h.slabId === option.slabId,
                      ),
                    }))
                    .filter((c) => c.hotels.length > 0);

                  const hotelMatch = matchingHotels?.[0]?.hotels?.[0];
                  const hotelImage =
                    hotelMatch?.images?.[0]?.imagePath || thumb;
                  return (
                    <>
                      <div key={option.slabId} className={styles.hotelCard}>
                        {hotelImage ? (
                          <Image
                            src={hotelImage}
                            alt={
                              hotelMatch?.images?.[0]?.alternateText ||
                              option?.slabName ||
                              ""
                            }
                            className={styles.hotelImage}
                            height={120}
                            width={120}
                            unoptimized
                            quality={70}
                            loading="lazy"
                          />
                        ) : (
                          <div className={styles.hotelImage} />
                        )}
                        <div className={styles.hoteldetails}>
                          <div>
                            {isDynamicPackage
                              ? starCount > 0
                                ? map(Array(starCount), (_, i) => (
                                  <MdOutlineStar
                                    key={i}
                                    style={{ color: "#121212" }}
                                  />
                                ))
                                : null
                              :
                              <span style={{ fontSize: "11px", color: "#121212" }}>{dayjs(option.fromDate).format("DD-MM-YYYY")} To {dayjs(option.toDate).format("DD-MM-YYYY")}</span>
                            }
                          </div>
                          <p className={styles.hotelName}>
                            {option?.slabName} Property
                          </p>
                          {matchingHotels?.length ? (
                            <ul className={styles.hotelList}>
                              {map(
                                expandedCards[option.slabId]
                                  ? matchingHotels
                                  : matchingHotels.slice(0, 3),
                                (hotel, index) => {
                                  const h = hotel.hotels[0];
                                  return (
                                    <li key={index} className={styles.listsize}>
                                      {h?.nights} Nights in {hotel.city} –{" "}
                                      {h?.hotelName}
                                    </li>
                                  );
                                },
                              )}
                              {matchingHotels.length > 3 ? (
                                <li
                                  className={styles.viewMoreBtn}
                                  onClick={() =>
                                    setExpandedCards((prev) => ({
                                      ...prev,
                                      [option.slabId]: !prev[option.slabId],
                                    }))
                                  }
                                >
                                  {expandedCards[option.slabId]
                                    ? "View Less"
                                    : "View More"}
                                </li>
                              ) : null}
                            </ul>
                          ) : null}
                        </div>
                        <div className={styles.hotelSelectButton}>
                          <button
                            type="button"
                            onClick={() => handleOpenBooking(option)}
                          >
                            Select
                          </button>
                        </div>
                      </div>
                      <div
                        key={option.slabId}
                        className={styles.hotelCardMobile}
                      >
                        <div className={styles.hotelCardTop}>
                          {hotelImage ? (
                            <Image
                              src={hotelImage}
                              alt={
                                hotelMatch?.images?.[0]?.alternateText ||
                                option?.slabName ||
                                ""
                              }
                              className={styles.hotelImage}
                              height={120}
                              width={120}
                              unoptimized
                              quality={70}
                              loading="lazy"
                            />
                          ) : (
                            <div className={styles.hotelImage} />
                          )}
                          <div>
                            {isDynamicPackage && starCount > 0
                              ? map(Array(starCount), (_, i) => (
                                <MdOutlineStar
                                  key={i}
                                  style={{ color: "#121212" }}
                                />
                              ))
                              : null}
                            <p className={styles.hotelName}>
                              {option?.slabName} Property
                            </p>
                          </div>
                        </div>
                        <div className={styles.hoteldetails}>
                          {matchingHotels?.length ? (
                            <ul className={styles.hotelList}>
                              {map(
                                expandedCards[option.slabId]
                                  ? matchingHotels
                                  : matchingHotels.slice(0, 3),
                                (hotel, index) => {
                                  const h = hotel.hotels[0];
                                  return (
                                    <li key={index} className={styles.listsize}>
                                      {h?.nights} Nights in {hotel.city} –{" "}
                                      {h?.hotelName}
                                    </li>
                                  );
                                },
                              )}
                              {matchingHotels.length > 3 ? (
                                <li
                                  className={styles.viewMoreBtn}
                                  onClick={() =>
                                    setExpandedCards((prev) => ({
                                      ...prev,
                                      [option.slabId]: !prev[option.slabId],
                                    }))
                                  }
                                >
                                  {expandedCards[option.slabId]
                                    ? "View Less"
                                    : "View More"}
                                </li>
                              ) : null}
                            </ul>
                          ) : null}
                        </div>
                        <div className={styles.hotelSelectButton}>
                          <button
                            type="button"
                            onClick={() => handleOpenBooking(option)}
                          >
                            Select
                          </button>
                        </div>
                      </div>
                    </>
                  );
                })}
              </div>
            </div>

            {/*HOLIDAY Inclusion / Exclusion */}
            <div
              ref={inclusionRef}
              id="detail-inclusion"
              className={styles.cardSection}
            >
              <button
                type="button"
                className={styles.cardHeader}
                onClick={() => setExclusionOpen((v) => !v)}
              >
                <span className={styles.overviewLabel} />
                <h2 className={styles.cardTitle}>Inclusions & Exclusions</h2>
                <IoChevronDown
                  size={24}
                  className={exclusionOpen ? styles.chevronOpen : ""}
                />
              </button>
              {exclusionOpen ? (
                <div className={`${styles.cardBody} ${styles.ieGrid}`}>
                  <div className={styles.ieCard}>
                    <h4>Inclusions</h4>
                    <ul>
                      {inclusions.map((item, index) => (
                        <li key={`inc-${index}`}>
                          <span className={`${styles.ieIcon} ${styles.ieYes}`}>
                            <FaCheck size={10} />
                          </span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className={styles.ieCard}>
                    <h4>Exclusions</h4>
                    <ul>
                      {exclusions.map((item, index) => (
                        <li key={`exc-${index}`}>
                          <span className={`${styles.ieIcon} ${styles.ieNo}`}>
                            <FaTimes size={10} />
                          </span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : null}
            </div>

            <PolicyAccordion policyData={pkg} />
          </div>

          {/* Sidebar */}
          <aside ref={sidebarRef} className={styles.sidebar}>
            <div
              className={`${styles.priceCard} ${styles.desktopSidebarPrice}`}
            >
              {showStickyTitle && pkg.packageName ? (
                <p className={styles.stickyPriceTitle}>{pkg.packageName}</p>
              ) : null}
              <div className={styles.priceTop}>
                {pkg.discountTag ? (
                  <span className={styles.offerTag}>{pkg.discountTag}</span>
                ) : (
                  <span />
                )}
                {pkg.reviews?.averageRating ? (
                  <div className={styles.ratingBadge}>
                    {pkg.reviews.averageRating}{" "}
                    <FaStar color="#fff" size={12} />
                    {pkg.reviews.totalReviews ? (
                      <span>({pkg.reviews.totalReviews} Reviews)</span>
                    ) : null}
                  </div>
                ) : null}
              </div>
              {priceOld > price ? (
                <p className={styles.priceOld}>
                  Starts From{" "}
                  <span className={styles.priceOldAmount}>
                    {getCurrencySymbol("sub", 10)}
                    {formatIndianPrice(priceOld)}
                  </span>
                </p>
              ) : null}
              <div className={styles.priceRow}>
                <div>
                  <p className={styles.priceNow}>
                    {getCurrencySymbol("main", 22)}
                    {formatIndianPrice(price)}
                  </p>
                </div>
                {priceOld > price ? (
                  <span className={styles.saveTag}>
                    Save &nbsp;{getCurrencySymbol("green", 8)}
                    {formatIndianPrice(priceOld - price)}
                  </span>
                ) : null}
              </div>
              <p className={styles.priceNote}>Per Person on Double Sharing</p>

              <button
                type="button"
                className={styles.proceedBtn}
                onClick={scrollToHotels}
              >
                Proceed
              </button>
              <button
                type="button"
                className={styles.enquiryBtn}
                onClick={() => setEnquiryOpen(true)}
              >
                Send Enquiry
              </button>
            </div>

            <InclusionsPanel
              pkg={pkg}
              nights={nights}
              days={days}
              className={`${styles.infoCard} ${styles.desktopInclusions}`}
            />

            <HolidayActionCards
              isIndia={isIndia}
              packageName={pkg.packageName}
              showAgent={false}
              onDownloadClick={() => setDownloadModalOpen(true)}
              className={styles.desktopActionCards}
            />
          </aside>
        </div>
        <div
          className={`standard-padding ${styles.mobilePriceBar} ${hideMobilePriceBar ? styles.mobilePriceBarHidden : ""
            }`}
        >
          <div className={styles.mobilePriceInfo}>
            {priceOld > price ? (
              <p className={styles.mobilePriceOld}>
                Starts From
                <span className={styles.mobilePriceOldSpan}>
                  {getCurrencySymbol("white", 9)}
                  {formatIndianPrice(priceOld)}
                </span>
              </p>
            ) : null}
            <p className={styles.mobilePriceNow}>
              {getCurrencySymbol("white", 15)}
              {formatIndianPrice(price)}
            </p>
            <p className={styles.mobilePriceNote}>/Person on DBL Sharing</p>
          </div>
          <div className={styles.mobilePriceActions}>
            <button
              type="button"
              className={styles.mobileEnquiryBtn}
              onClick={() => setEnquiryOpen(true)}
            >
              Send Enquiry
            </button>
            <button
              type="button"
              className={styles.mobileProceedBtn}
              onClick={scrollToHotels}
            >
              Proceed
            </button>
          </div>
        </div>

        <ShareModal
          show={showShareModal}
          onHide={() => setShowShareModal(false)}
          shareUrl={shareData.url}
          title={shareData.title}
        />

        <DownloadItineraryModal
          open={downloadModalOpen}
          onClose={() => setDownloadModalOpen(false)}
          packageId={pkg.packageId}
          packageName={pkg.packageName}
          itineraryText={itinerarySummary}
          nights={nights}
          image={banner || thumb}
          badgeLabel={
            groupedPrices[0]?.slabName
              ? `${groupedPrices[0].slabName} Property`
              : ""
          }
        />

        <EnquiryModal
          open={enquiryOpen}
          onClose={() => setEnquiryOpen(false)}
          moduleType={ENQUIRY_MODULE.HOLIDAY}
          title={pkg.packageName}
          subtitle={itinerarySummary}
          badgeLabel={
            groupedPrices[0]?.slabName
              ? `${groupedPrices[0].slabName} Property`
              : nights
                ? `${nights}N/${days}D`
                : ""
          }
          image={banner || thumb}
          price={price}
          cutPrice={priceOld}
          discountTag={pkg.discountTag}
          pageUrlName={pkg.packageName}
          defaultNights={nights || 2}
          defaultTravelers={2}
        />

        <BookingModal
          open={bookingOpen}
          onClose={() => setBookingOpen(false)}
          title={pkg.packageName}
          packageDetail={packageDetail}
          itinerary={itinerarySummary}
          image={selectedSlab?.image || banner || thumb}
          slabLabel={selectedSlab?.slabLabel || ""}
          slabId={selectedSlab?.slabId}
          propertyName={selectedSlab?.slabName || ""}
          packageData={groupedPrices}
          slabPrices={selectedSlab?.prices || []}
          packageId={pkg.packageId}
          isDynamicPackage={isDynamicPackage}
          isSpecialPackage={Boolean(pkg.isSpecialPackage)}
          isIndia={isIndia}
          fromDate={selectedSlab?.fromDate || ""}
          toDate={selectedSlab?.toDate || ""}
        />
      </div>
      <CustomerReviewsFull
        moduleType={"Holiday"}
        slug={slug}
        initialReviewsList={reviewsList}
        skipAutoFetch
        onReviewsReload={loadReviews}
      />
      {similarSectionData?.length ? (
        <HolidayPackageSection
          title="Similar Holiday Packages"
          subtitle="Holidays our experts actually took"
          data={similarSectionData}
          hideWhenEmpty
        />
      ) : null}
      <DownloadNH />
    </MainLayout>
  );
}
